import React, { useMemo, useState } from "react";
import { Box, Button, Grid, MenuItem, Modal, Select, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import moment from "moment";
import CircularLoading from "../components/CircularLoading";
import BookingDataLoadError from "../components/BookingDataLoadError";

import { colors } from "../components/Theme/WebTheme";
import localization from "../components/Table/Localization";
import AdminDataTable from "../components/AdminDataTable";
import AdminStatusBadge from "../components/AdminStatus";
import { ActionButton, PageHeader, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";
import {
  FONT_FAMILY,
  formatCurrency,
  getBookingStatusLabel,
  getCommissionPreview,
  getEarningsPreview,
  getFinalPaymentAmount,
  getFleetAdminDisplayName,
  getFleetCompanyType,
  labelText
} from "../common/sharedFunctions";

const firstPresent = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const objectLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return value.toString();
  return value.name || value.carType || value.vehicleTypeName || value.label || "";
};

const normalizeList = (offers) => {
  if (!offers) return [];
  const entries = Array.isArray(offers)
    ? offers.map((offer, index) => [offer && (offer.id || offer.uid || index), offer])
    : Object.entries(offers);
  return entries
    .map(([id, offer]) => ({ id, ...(offer || {}) }))
    .filter((offer) => offer && offer.status !== "cancelled");
};

const getBookingDate = (booking) => firstPresent(
  booking && booking.bookingDate,
  booking && booking.tripdate,
  booking && booking.createdAt,
  booking && booking.created_at,
  booking && booking.date
);

const formatDateTime = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("lll") : value;
};

const getBookingAddressText = (booking, pointKey, flatKey) => {
  const point = booking && booking[pointKey] ? booking[pointKey] : {};
  return point.add || point.description || point.address || (booking && booking[flatKey]) || "";
};

const getCustomerName = (booking) => (
  booking.customer_name ||
  booking.customerName ||
  booking.customer_fullname ||
  [booking.customer_firstName, booking.customer_lastName].filter(Boolean).join(" ").trim() ||
  booking.customer_email ||
  booking.customer ||
  ""
);

const getDriverName = (booking) => (
  booking.driver_name ||
  booking.driverName ||
  booking.driver_fullname ||
  booking.driver_email ||
  booking.driver ||
  ""
);

const getOfferAmount = (offer) => firstPresent(
  offer && offer.trip_cost,
  offer && offer.bidAmount,
  offer && offer.amount,
  offer && offer.offerAmount,
  offer && offer.price
);

const getDriverOfferName = (offer) => (
  offer.driver_name ||
  offer.driverName ||
  offer.name ||
  offer.displayName ||
  offer.email ||
  offer.driverId ||
  offer.id ||
  ""
);

const getFleetOfferName = (offer) => (
  offer.companyName ||
  offer.company_name ||
  getFleetAdminDisplayName(offer) ||
  offer.fleetAdminId ||
  offer.id ||
  ""
);

const getPaymentStatus = (booking) => (
  booking.payment_status ||
  booking.paymentStatus ||
  booking.payment_status_text ||
  (booking.paid ? "Paid" : "") ||
  ""
);

const isYes = (value) => value === true || value === "true" || value === "yes" || value === 1;

const getCommissionDeductedStatus = (booking) => (
  isYes(booking.garahexCommissionDeducted || booking.driverAcceptanceFeeDeducted || booking.commissionDeducted)
    ? "Commission Deducted"
    : "Commission Pending"
);

const getEarningRecordedStatus = (booking) => {
  const recorded = booking.acceptedOfferType === "fleet"
    ? (booking.fleetAdminEarningRecorded || booking.earningRecorded)
    : (booking.driverEarningRecorded || booking.earningRecorded);
  return isYes(recorded) ? "Earning Recorded" : "Earning Pending";
};

const getAcceptedOfferTypeLabel = (booking) => {
  if (booking.acceptedOfferType === "fleet") return "Fleet";
  if (booking.acceptedOfferType === "driver") return "Driver";
  return "None";
};

const getOfferStatusLabel = (booking, fleetOffers, driverOffers) => {
  if (booking.acceptedOfferType === "fleet") {
    return booking.driver ? "Driver Assigned" : "Fleet Bid Accepted";
  }
  if (booking.acceptedOfferType === "driver") return "Driver Bid Accepted";
  if ((booking.status || "").toString().toUpperCase() === "AWAITING_DRIVER_ASSIGNMENT") return "Awaiting Driver Assignment";
  if (fleetOffers.length > 0 && driverOffers.length > 0) return "Fleet and Driver Bids Submitted";
  if (fleetOffers.length > 0) return "Fleet Bid Submitted";
  if (driverOffers.length > 0) return "Driver Bid Submitted";
  if (booking.deliveryWithBid === true) return "Open for Bid";
  return "";
};

const getAcceptedFleetName = (booking) => {
  const offer = booking.acceptedFleetOffer || {};
  return booking.acceptedOfferType === "fleet"
    ? (getFleetOfferName(offer) || booking.fleetadminName || booking.acceptedFleetAdmin || booking.fleetadmin || "")
    : "";
};

const getAcceptedDriverName = (booking) => {
  const offer = booking.acceptedDriverOffer || booking.selectedBid || {};
  return booking.acceptedOfferType === "driver"
    ? (getDriverOfferName(offer) || getDriverName(booking))
    : "";
};

const isBidBooking = (booking) => (
  booking &&
  booking.bookingSource !== "fleet_admin_client" &&
  booking.visibility !== "private" &&
  booking.privateBooking !== true &&
  (
    booking.deliveryWithBid === true ||
    booking.acceptedOfferType ||
    booking.fleetOffers ||
    booking.driverOffers ||
    booking.acceptedFleetOffer ||
    booking.acceptedDriverOffer ||
    booking.selectedBid
  )
);

const buildReportRow = (booking, id, settings, t) => {
  const fleetOffers = normalizeList(booking.fleetOffers);
  const driverOffers = normalizeList(booking.driverOffers);
  const paymentAmount = getFinalPaymentAmount(booking);
  const grossAmount = paymentAmount.pending ? null : paymentAmount.amount;
  const commission = getCommissionPreview(booking);
  const earnings = getEarningsPreview(booking);
  const bookingDate = getBookingDate(booking);
  const offerStatus = getOfferStatusLabel(booking, fleetOffers, driverOffers);

  return {
    id,
    booking,
    fleetOffers,
    driverOffers,
    reference: booking.reference || booking.bookingNumber || id,
    bookingDate,
    bookingDateText: formatDateTime(bookingDate),
    customerName: getCustomerName(booking),
    shipmentType: booking.shipmentType || booking.shipmentCategory || booking.deliveryType || "",
    serviceType: booking.serviceType || booking.service_type || "",
    vehicleType: objectLabel(booking.carType || booking.vehicleType || booking.vehicle_type),
    pickupAddress: getBookingAddressText(booking, "pickup", "pickupAddress"),
    deliveryAddress: getBookingAddressText(booking, "drop", "dropAddress"),
    bookingStatus: getBookingStatusLabel(booking, "admin", t, {}),
    offerStatus,
    acceptedOfferType: getAcceptedOfferTypeLabel(booking),
    acceptedFleetName: getAcceptedFleetName(booking),
    acceptedDriverName: getAcceptedDriverName(booking),
    assignedDriverName: getDriverName(booking),
    bidAmount: grossAmount,
    bidAmountText: grossAmount === null ? "Waiting for bid acceptance" : formatCurrency(grossAmount, settings),
    garahexCommissionAmount: commission.commissionAmount,
    garahexCommissionText: commission.commissionAmount === null || commission.commissionAmount === undefined ? "Pending" : formatCurrency(commission.commissionAmount, settings),
    netPayeeEarning: earnings.netAmount,
    netPayeeEarningText: earnings.netAmount === null || earnings.netAmount === undefined ? "Pending" : formatCurrency(earnings.netAmount, settings),
    paymentMethod: booking.payment_mode || booking.paymentMode || "",
    paymentStatus: getPaymentStatus(booking),
    commissionDeductedStatus: getCommissionDeductedStatus(booking),
    earningRecordedStatus: getEarningRecordedStatus(booking),
    acceptedAt: formatDateTime(firstPresent(booking.acceptedAt, booking.accepted_at, booking.updatedAt)),
    assignedAt: formatDateTime(booking.assignedAt),
    assignedByFleetAdmin: booking.assignedByFleetAdmin || "",
    searchText: [
      booking.reference,
      booking.bookingNumber,
      getCustomerName(booking),
      getAcceptedFleetName(booking),
      getAcceptedDriverName(booking),
      getDriverName(booking),
      objectLabel(booking.carType || booking.vehicleType || booking.vehicle_type)
    ].filter(Boolean).join(" ").toLowerCase()
  };
};

export default function AdminBidMonitoring() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const label = (key, fallback) => labelText(t, key, fallback);
  const navigate = useNavigate();
  const settings = useSelector((state) => state.settingsdata.settings);
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const bookings = useMemo(() => (
    (bookinglistdata.bookings || []).filter(isBidBooking).slice().reverse()
  ), [bookinglistdata.bookings]);
  const loading = bookinglistdata.loading;
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsRow, setDetailsRow] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    bookingStatus: "",
    offerStatus: "",
    acceptedOfferType: "",
    fleetAdmin: "",
    driver: "",
    vehicleType: "",
    paymentStatus: "",
    commissionDeducted: "",
    earningRecorded: ""
  });


  const rows = useMemo(() => (
    bookings.map((booking) => buildReportRow(booking, booking.id, settings, t))
  ), [bookings, settings, t]);

  const filterOptions = useMemo(() => {
    const unique = (field) => Array.from(new Set(rows.map((row) => row[field]).filter(Boolean))).sort();
    return {
      bookingStatus: unique("bookingStatus"),
      offerStatus: unique("offerStatus"),
      acceptedOfferType: ["Fleet", "Driver", "None"],
      fleetAdmin: unique("acceptedFleetName"),
      driver: unique("acceptedDriverName").concat(unique("assignedDriverName")).filter(Boolean).filter((value, index, arr) => arr.indexOf(value) === index).sort(),
      vehicleType: unique("vehicleType"),
      paymentStatus: unique("paymentStatus"),
      commissionDeducted: ["Commission Deducted", "Commission Pending"],
      earningRecorded: ["Earning Recorded", "Earning Pending"]
    };
  }, [rows]);

  const filteredRows = useMemo(() => rows.filter((row) => {
    const bookingMoment = row.bookingDate ? moment(row.bookingDate) : null;
    if (filters.dateFrom && (!bookingMoment || bookingMoment.isBefore(moment(filters.dateFrom).startOf("day")))) return false;
    if (filters.dateTo && (!bookingMoment || bookingMoment.isAfter(moment(filters.dateTo).endOf("day")))) return false;
    if (filters.bookingStatus && row.bookingStatus !== filters.bookingStatus) return false;
    if (filters.offerStatus && row.offerStatus !== filters.offerStatus) return false;
    if (filters.acceptedOfferType && row.acceptedOfferType !== filters.acceptedOfferType) return false;
    if (filters.fleetAdmin && row.acceptedFleetName !== filters.fleetAdmin) return false;
    if (filters.driver && row.acceptedDriverName !== filters.driver && row.assignedDriverName !== filters.driver) return false;
    if (filters.vehicleType && row.vehicleType !== filters.vehicleType) return false;
    if (filters.paymentStatus && row.paymentStatus !== filters.paymentStatus) return false;
    if (filters.commissionDeducted && row.commissionDeductedStatus !== filters.commissionDeducted) return false;
    if (filters.earningRecorded && row.earningRecordedStatus !== filters.earningRecorded) return false;
    return true;
  }), [rows, filters]);

  const setFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const resetFilters = () => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      bookingStatus: "",
      offerStatus: "",
      acceptedOfferType: "",
      fleetAdmin: "",
      driver: "",
      vehicleType: "",
      paymentStatus: "",
      commissionDeducted: "",
      earningRecorded: ""
    });
  };

  const renderSelectFilter = (field, labelName, values) => (
    <Grid item xs={12} sm={6} md={3}>
      <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, marginBottom: 4 }}>{labelName}</Typography>
      <Select fullWidth value={filters[field]} onChange={(event) => setFilter(field, event.target.value)} displayEmpty>
        <MenuItem value="">{label("all", "All")}</MenuItem>
        {values.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
      </Select>
    </Grid>
  );

  const columns = [
    { title: label("booking_ref", "Booking Reference"), field: "reference" },
    { title: label("booking_date", "Booking Date"), field: "bookingDateText" },
    { title: label("customer_name", "Customer Name"), field: "customerName" },
    { title: label("shipment_type", "Shipment Type"), field: "shipmentType" },
    { title: label("service_type", "Service Type"), field: "serviceType" },
    { title: label("car_type", "Vehicle Type"), field: "vehicleType" },
    { title: label("pickup_address", "Pickup Address"), field: "pickupAddress" },
    { title: label("delivery_address", "Delivery Address"), field: "deliveryAddress" },
    {
      title: label("booking_status_web", "Booking Status"),
      field: "bookingStatus",
      render: (rowData) => <AdminStatusBadge status={rowData.bookingStatus} />
    },
    {
      title: label("offer_status", "Offer Status"),
      field: "offerStatus",
      render: (rowData) => <AdminStatusBadge status={rowData.offerStatus || label("no_offer", "No Offer")} />
    },
    { title: label("accepted_offer_type", "Accepted Offer Type"), field: "acceptedOfferType" },
    { title: label("accepted_fleet_admin", "Accepted Fleet Admin / Logistics Company"), field: "acceptedFleetName" },
    { title: label("accepted_driver", "Accepted Driver"), field: "acceptedDriverName" },
    { title: label("assigned_driver", "Assigned Driver"), field: "assignedDriverName" },
    { title: label("bid_amount", "Bid Amount"), field: "bidAmountText", customSort: (a, b) => (a.bidAmount || 0) - (b.bidAmount || 0) },
    { title: label("garahex_commission", "Garahex Commission Amount"), field: "garahexCommissionText", customSort: (a, b) => (a.garahexCommissionAmount || 0) - (b.garahexCommissionAmount || 0) },
    { title: label("net_payee_earning", "Net Payee Earning"), field: "netPayeeEarningText", customSort: (a, b) => (a.netPayeeEarning || 0) - (b.netPayeeEarning || 0) },
    { title: label("payment_mode", "Payment Method"), field: "paymentMethod" },
    { title: label("payment_status", "Payment Status"), field: "paymentStatus" },
    { title: label("commission_deducted_status", "Commission Deducted Status"), field: "commissionDeductedStatus" },
    { title: label("earning_recorded_status", "Earning Recorded Status"), field: "earningRecordedStatus" },
    {
      title: label("action", "Actions / View Details"),
      field: "action",
      sorting: false,
      filtering: false,
      render: (rowData) => (
        <Box style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="contained"
            style={{ backgroundColor: colors.Action_Button_Back, color: colors.Header_Background, fontFamily: FONT_FAMILY, textTransform: "none" }}
            onClick={() => setDetailsRow(rowData)}
          >
            {label("view_offer_details", "Offer Details")}
          </Button>
          <Button
            size="small"
            variant="contained"
            style={{ backgroundColor: colors.Header_Background, color: colors.WHITE, fontFamily: FONT_FAMILY, textTransform: "none" }}
            onClick={() => navigate(`/bookings/bookingdetails/${rowData.id}`)}
          >
            {label("booking_details", "View Details")}
          </Button>
        </Box>
      )
    }
  ];

  if (bookinglistdata.error && bookinglistdata.error.flag) {
    return <BookingDataLoadError message={bookinglistdata.error.msg} />;
  }

  if (loading) return <CircularLoading />;

  const bidSummary = [
    {
      label: label("marketplace_bookings", "Marketplace Bookings"),
      value: rows.length,
      tone: "info",
      helper: label("visible_bid_scope", "Visible bid scope")
    },
    {
      label: label("open_for_bid", "Open for Bid"),
      value: rows.filter((row) => (row.offerStatus || "").toLowerCase().indexOf("open") !== -1).length,
      tone: "purple",
      helper: label("awaiting_offers", "Awaiting offers")
    },
    {
      label: label("accepted_bids", "Accepted Bids"),
      value: rows.filter((row) => row.acceptedOfferType && row.acceptedOfferType !== "None").length,
      tone: "success",
      helper: label("matched_shipments", "Matched shipments")
    },
    {
      label: label("filtered_results", "Filtered Results"),
      value: filteredRows.length,
      tone: "warning",
      helper: label("current_view", "Current view")
    }
  ];

  return (
    <Box className="garahex-marketplace-page">
      <PageHeader
        eyebrow={label("garahex_marketplace", "Garahex Marketplace")}
        title={label("admin_bid_monitoring", "Admin Bid Monitoring")}
        description={label("admin_bid_monitoring_description", "Review marketplace bids, accepted offers, commissions, payment states, and assignment readiness without changing the booking workflow.")}
        actions={<StatusBadge tone="info">{`${filteredRows.length} ${label("records", "records")}`}</StatusBadge>}
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {bidSummary.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <SurfaceCard sx={{ minHeight: 128 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start" }}>
                <Box>
                  <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ color: adminTokens.colors.text, fontSize: "2rem", fontWeight: 950, lineHeight: 1.05, mt: 0.8 }}>
                    {item.value}
                  </Typography>
                  <Typography sx={{ color: adminTokens.colors.muted, fontSize: 12, mt: 0.75 }}>
                    {item.helper}
                  </Typography>
                </Box>
                <StatusBadge tone={item.tone}>{item.tone}</StatusBadge>
              </Box>
            </SurfaceCard>
          </Grid>
        ))}
      </Grid>

      <SurfaceCard sx={{ mb: 2 }}>
        <SectionHeader
          title={label("marketplace_filters", "Marketplace Filters")}
          description={label("marketplace_filters_description", "Use the existing filters to narrow bids by status, fleet, driver, vehicle, payment, and earnings state.")}
        />
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField fullWidth type="date" label={label("date_from", "Date From")} value={filters.dateFrom} onChange={(event) => setFilter("dateFrom", event.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField fullWidth type="date" label={label("date_to", "Date To")} value={filters.dateTo} onChange={(event) => setFilter("dateTo", event.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        {renderSelectFilter("bookingStatus", label("booking_status_web", "Booking Status"), filterOptions.bookingStatus)}
        {renderSelectFilter("offerStatus", label("offer_status", "Offer Status"), filterOptions.offerStatus)}
        {renderSelectFilter("acceptedOfferType", label("accepted_offer_type", "Accepted Offer Type"), filterOptions.acceptedOfferType)}
        {renderSelectFilter("fleetAdmin", label("fleet_admin_company", "Fleet Admin / Logistics Company"), filterOptions.fleetAdmin)}
        {renderSelectFilter("driver", label("driver", "Driver"), filterOptions.driver)}
        {renderSelectFilter("vehicleType", label("car_type", "Vehicle Type"), filterOptions.vehicleType)}
        {renderSelectFilter("paymentStatus", label("payment_status", "Payment Status"), filterOptions.paymentStatus)}
        {renderSelectFilter("commissionDeducted", label("commission_deducted", "Commission Deducted"), filterOptions.commissionDeducted)}
        {renderSelectFilter("earningRecorded", label("earning_recorded", "Earning Recorded"), filterOptions.earningRecorded)}
        <Grid item xs={12} sm={6} md={3} style={{ display: "flex", alignItems: "flex-end" }}>
          <ActionButton onClick={resetFilters} sx={{ background: adminTokens.colors.danger }}>
            {label("reset_filters", "Reset Filters")}
          </ActionButton>
        </Grid>
        </Grid>
      </SurfaceCard>

      <SurfaceCard>
        <SectionHeader
          title={label("bid_monitoring_report", "Bid Monitoring Report")}
          description={label("bid_monitoring_report_description", "Existing search, export, sorting, pagination, and details actions remain active.")}
        />
        <AdminDataTable
          title={label("bid_monitoring_report", "Bid Monitoring Report")}
          columns={columns}
          data={filteredRows}
          emptyTitle={label("no_bid_bookings_found", "No marketplace bookings found")}
          emptyDescription={label("no_bid_bookings_found_description", "No bid-enabled bookings match the current filters.")}
          onRowClick={(event, rowData) => setSelectedRow(rowData.tableData.id)}
          style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
          }}
          options={{
            exportButton: true,
            search: true,
            pageSize: 10,
            pageSizeOptions: [10, 20, 50, 100],
            rowStyle: (rowData) => ({
              backgroundColor: selectedRow === rowData.tableData.id ? "rgba(11, 124, 255, 0.09)" : colors.WHITE
            }),
          }}
          localization={localization(t)}
        />
      </SurfaceCard>

      <OfferDetailsModal
        row={detailsRow}
        settings={settings}
        label={label}
        onClose={() => setDetailsRow(null)}
      />
    </Box>
  );
}

function OfferDetailsModal({ row, settings, label, onClose }) {
  if (!row) return null;
  const detailLine = (title, value) => (
    <Grid item xs={12} sm={6}>
      <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: colors.DRIVER_TRIPS_TEXT }}>
        {title}
      </Typography>
      <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 600, wordBreak: "break-word" }}>
        {value || "-"}
      </Typography>
    </Grid>
  );
  const renderOffer = (offer, type) => (
    <Box key={`${type}-${offer.id}`} style={{ border: `1px solid ${adminTokens.colors.border}`, borderRadius: 14, padding: 12, marginBottom: 10, background: "#F7FBFF" }}>
      <Grid container spacing={1}>
        {detailLine(type === "fleet" ? label("fleet_admin_uid", "Fleet Admin UID") : label("driver_uid", "Driver UID"), offer.fleetAdminId || offer.driverId || offer.id)}
        {detailLine(type === "fleet" ? label("company_name", "Company Name") : label("driver_name", "Driver Name"), type === "fleet" ? getFleetOfferName(offer) : getDriverOfferName(offer))}
        {type === "driver" ? detailLine(label("driver_fleet_admin", "Driver Fleet Admin"), offer.fleetadmin || offer.fleetAdmin || "") : detailLine(label("company_type", "Company Type"), getFleetCompanyType(offer))}
        {detailLine(label("bid_amount", "Bid Amount"), getOfferAmount(offer) !== undefined ? formatCurrency(getOfferAmount(offer), settings) : "")}
        {detailLine(label("remarks", "Remarks"), offer.note || offer.remarks || "")}
        {detailLine(label("offer_status", "Offer Status"), offer.status || "")}
        {detailLine(label("created_at", "Created At"), formatDateTime(offer.createdAt || offer.created_at))}
        {detailLine(label("updated_at", "Updated At"), formatDateTime(offer.updatedAt || offer.updated_at))}
        {detailLine(label("accepted", "Accepted"), row.acceptedOfferType.toLowerCase() === type && (row.acceptedFleetName === getFleetOfferName(offer) || row.acceptedDriverName === getDriverOfferName(offer)) ? "Yes" : "No")}
      </Grid>
    </Box>
  );

  return (
    <Modal open={!!row} onClose={onClose}>
      <Box
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(900px, calc(100vw - 32px))",
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: colors.WHITE,
          border: `1px solid ${adminTokens.colors.border}`,
          borderRadius: 8,
          padding: 20
        }}
      >
        <Typography variant="h6" style={{ fontFamily: FONT_FAMILY, fontWeight: "bold", marginBottom: 12 }}>
          {label("offer_details", "Offer Details")} - {row.reference}
        </Typography>

        <Typography variant="subtitle1" style={{ fontFamily: FONT_FAMILY, fontWeight: "bold", marginBottom: 8 }}>
          {label("accepted_offer", "Accepted Offer")}
        </Typography>
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          {detailLine(label("accepted_offer_type", "Accepted Offer Type"), row.acceptedOfferType)}
          {detailLine(label("accepted_fleet_admin", "Accepted Fleet Admin / Logistics Company"), row.acceptedFleetName)}
          {detailLine(label("accepted_driver", "Accepted Driver"), row.acceptedDriverName)}
          {detailLine(label("accepted_bid_amount", "Accepted Bid Amount"), row.bidAmountText)}
          {detailLine(label("accepted_at", "Accepted At"), row.acceptedAt)}
        </Grid>

        <Typography variant="subtitle1" style={{ fontFamily: FONT_FAMILY, fontWeight: "bold", marginBottom: 8 }}>
          {label("assignment", "Assignment")}
        </Typography>
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          {detailLine(label("assigned_driver", "Assigned Driver"), row.assignedDriverName)}
          {detailLine(label("assigned_at", "Assigned At"), row.assignedAt)}
          {detailLine(label("assigned_by_fleet_admin", "Assigned By Fleet Admin"), row.assignedByFleetAdmin)}
        </Grid>

        <Typography variant="subtitle1" style={{ fontFamily: FONT_FAMILY, fontWeight: "bold", marginBottom: 8 }}>
          {label("fleet_offers", "Fleet Offers")}
        </Typography>
        {row.fleetOffers.length > 0 ? row.fleetOffers.map((offer) => renderOffer(offer, "fleet")) : (
          <Typography style={{ fontFamily: FONT_FAMILY, marginBottom: 16 }}>{label("no_fleet_offers", "No fleet offers")}</Typography>
        )}

        <Typography variant="subtitle1" style={{ fontFamily: FONT_FAMILY, fontWeight: "bold", marginBottom: 8 }}>
          {label("driver_offers", "Driver Offers")}
        </Typography>
        {row.driverOffers.length > 0 ? row.driverOffers.map((offer) => renderOffer(offer, "driver")) : (
          <Typography style={{ fontFamily: FONT_FAMILY, marginBottom: 16 }}>{label("no_driver_offers", "No driver offers")}</Typography>
        )}

        <Box style={{ textAlign: "right", marginTop: 16 }}>
          <Button variant="contained" onClick={onClose} style={{ backgroundColor: colors.Header_Background, color: colors.WHITE, fontFamily: FONT_FAMILY, textTransform: "none" }}>
            {label("close", "Close")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
